import { createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { combineReducers } from 'redux';
import { all, call, cancelled, put, select, takeLatest } from 'redux-saga/effects';
import { FETCH_PRODUCT_MAIN_INFO } from 'features/product-info/ducks/product-main-info';
import {
  getIsPricesSwapped,
  getPrimaryPrice,
  getSecondPrice,
  getSource,
} from 'features/product-info/ducks/selectors';
import { getRegionCode, getArticle } from 'common/selectors';
import { api } from 'api';
import { EffectR } from 'types/utils';
import { getLocale, getLang } from 'utils';
import { fetchGoodsSupplyData } from './api';
import { GetStocksRequestBody, GoodsSupplyCase, RTPData } from './types';
import { getGoodsSupplyData } from './selectors';
import { processResponse } from './utils';

export const FETCH_GOODS_SUPPLY_DATA = createAsyncAction(
  'supply/FETCH_GOODS_SUPPLY_DATA/request',
  'supply/FETCH_GOODS_SUPPLY_DATA/success',
  'supply/FETCH_GOODS_SUPPLY_DATA/failure',
  'supply/FETCH_GOODS_SUPPLY_DATA/cancel',
)<GetStocksRequestBody, RTPData, Response | Error, void>();

export const RESET_GOODS_SUPPLY_DATA = createAction('supply/FETCH_GOODS_SUPPLY_DATA/reset')();
export const SET_GOODS_SUPPLY_CASE = createAction(
  'supply/SET_GOODS_SUPPLY_CASE/set',
)<GoodsSupplyCase | null>();

const goodsSupplyData = createReducer<RTPData | null>(null)
  .handleAction(
    [
      // FETCH_GOODS_SUPPLY_DATA.request,
      FETCH_GOODS_SUPPLY_DATA.failure,
      FETCH_GOODS_SUPPLY_DATA.cancel,
    ],
    () => null,
  )
  .handleAction(FETCH_GOODS_SUPPLY_DATA.success, (_, { payload }) => payload);

const goodsSupplyIsLoading = createReducer<boolean>(false)
  .handleAction([FETCH_GOODS_SUPPLY_DATA.request], () => true)
  .handleAction(
    [FETCH_GOODS_SUPPLY_DATA.cancel, FETCH_GOODS_SUPPLY_DATA.failure, FETCH_GOODS_SUPPLY_DATA.success],
    () => false,
  );

const goodsSupplyCase = createReducer<GoodsSupplyCase | null>(null).handleAction(
  [SET_GOODS_SUPPLY_CASE],
  (_, { payload }) => payload,
);

const goodsSupplyError = createReducer<Response | Error | null | string>(null)
  .handleAction([FETCH_GOODS_SUPPLY_DATA.failure], (_, { payload }) => payload)
  .handleAction([FETCH_GOODS_SUPPLY_DATA.request, FETCH_GOODS_SUPPLY_DATA.success], () => null);

export const goodsSupply = combineReducers({
  goodsSupplyData,
  isLoading: goodsSupplyIsLoading,
  error: goodsSupplyError,
  supplyCase: goodsSupplyCase,
});

export function* runSupplyDataRefetch() {
  const regionCode = (yield select(getRegionCode)) as EffectR.Select<typeof getRegionCode>;
  const productId = (yield select(getArticle)) as EffectR.Select<typeof getArticle>;
  const isPricesSwapped = (yield select(getIsPricesSwapped)) as EffectR.Select<typeof getIsPricesSwapped>;
  const primaryPrice = (yield select(getPrimaryPrice)) as EffectR.Select<typeof getPrimaryPrice>;
  const secondPrice = (yield select(getSecondPrice)) as EffectR.Select<typeof getSecondPrice>;
  const lang = getLang();
  const locale = getLocale(lang);

  const unit = (isPricesSwapped ? secondPrice.unit : primaryPrice.unit) || '';

  const source = ((yield select(getSource)) as EffectR.Select<typeof getSource>) || '';
  const currencyKey = primaryPrice.currency;

  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  yield put(FETCH_GOODS_SUPPLY_DATA.request());

  if (currencyKey) {
    try {
      const data = (yield call(
        fetchGoodsSupplyData,
        {
          regionCode,
          productId,
          unit,
          currencyKey,
          preferedStores: [],
          source,
        },
        abortController.token,
      )) as EffectR.Call<typeof fetchGoodsSupplyData>;

      const payload: RTPData = yield call(processResponse, data, locale);

      yield put(FETCH_GOODS_SUPPLY_DATA.success(payload));
    } catch (e) {
      if (e instanceof Error) {
        yield put(FETCH_GOODS_SUPPLY_DATA.failure(e));
      }
    } finally {
      if ((yield cancelled()) as boolean) {
        yield put(FETCH_GOODS_SUPPLY_DATA.cancel());
        abortController.cancel();
      }
    }
  }
}

export function* rootSaga() {
  yield all([
    put(RESET_GOODS_SUPPLY_DATA()),
    takeLatest([FETCH_PRODUCT_MAIN_INFO.success, RESET_GOODS_SUPPLY_DATA], () => runSupplyDataRefetch()),
    takeLatest([FETCH_GOODS_SUPPLY_DATA.success], () => measureSupplyCase()),
  ]);
}

export function* measureSupplyCase() {
  const sc = ((yield select(getGoodsSupplyData)) as EffectR.Select<typeof getGoodsSupplyData>)?.longtailCase;
  const caseKey = `${sc?.source}:${sc?.isAvailableOnline}:${sc?.isAvailableOffline}`;
  yield put(SET_GOODS_SUPPLY_CASE(SupplyCaseMap.get(caseKey) || null));
}

const SupplyCaseMap = new Map<string, GoodsSupplyCase>([
  ['E-COMMERCE:false:false', 'CASE_1'],
  ['E-COMMERCE:false:true', 'CASE_2'],
  ['E-COMMERCE:true:false', 'CASE_3'],
  ['E-COMMERCE:true:true', 'CASE_4'],

  ['MARKETPLACE:false:false', 'CASE_5'],
  ['MARKETPLACE:false:true', 'CASE_6'],
  ['MARKETPLACE:true:false', 'CASE_7'],
  ['MARKETPLACE:true:true', 'CASE_8'],
]);
